import type { SerializedStyles, Theme } from "@emotion/react";
import type { ReactNode } from "react";

import { Link } from "@aviary";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import type { BrandSplinter } from "@shared/data/queries/BrandsQuery/Brands.query";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { EmersonSearchApplyFilterEntryPoint } from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import {
  FilterType,
  useAnalyticsLogger,
} from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";

interface Props {
  brand: Pick<BrandSplinter, "id" | "name">;
  tabIndex?: number;
  linkStyles?:
    | SerializedStyles
    | (SerializedStyles | ((theme: Theme) => SerializedStyles))[]
    | ((theme: Theme) => SerializedStyles);
  children?: ReactNode;
  onClick?: () => void;
  entryPoint: EmersonSearchApplyFilterEntryPoint;
  isUnderLined?: boolean;
  isHoverUnderLined?: boolean;
  isColor?: "success" | "system";
}

const BrandLink = ({
  tabIndex,
  brand,
  linkStyles,
  onClick,
  entryPoint,
  isUnderLined = true,
  isHoverUnderLined = true,
  isColor = "success",
  children,
}: Props) => {
  const logAnalyticsEvent = useAnalyticsLogger();

  const getBrandsPath = () => {
    const { id, name } = brand;

    return emersonRoutes.getBrand(name, id);
  };

  if (!brand) return null;

  const onBrandClick = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_SEARCH_APPLY_FILTER,
      payload: {
        entry_point: entryPoint,
        content: brand.name,
        filter_type: FilterType.BRAND,
        search_version: 2,
      },
    });

    onClick?.();
  };

  return (
    <Link
      tabIndex={tabIndex}
      key={brand.id}
      to={getBrandsPath()}
      onClick={onBrandClick}
      css={linkStyles}
      isUnderLined={isUnderLined}
      isHoverUnderLined={isHoverUnderLined}
      isColor={isColor}
      data-testid="brand-link"
    >
      {children}
    </Link>
  );
};

export { BrandLink };
